/**
 * The mission board.
 *
 * Two halves, and the order matters: what you are *carrying for this station*
 * comes first, then what is on offer. A player walking into the station they
 * were flying to wants one button, and it should not be below four jobs they
 * are not doing.
 *
 * ── Accepting, twice ────────────────────────────────────────────────────────
 *
 * `accept` and `accept and route` do the same thing to the hold and differ in
 * one respect: the second also pins the destination, so the cockpit's compass
 * and the sky's markers point at it the moment you undock. Two buttons rather
 * than one because taking a job while already running another is a real thing
 * to do, and silently repointing navigation at the new one would quietly
 * abandon the trip you were on.
 */

import {
  MISSION_LABELS,
  formatLinelits,
  formatRemaining,
  stationMissions,
  tutorialBoard,
  missionExpired,
  missionRedeemable,
  missionRemaining,
  stow,
  type CargoItem,
  type MissionBrief,
  type MissionCargo,
  type Wallet,
} from '@interspacial/shared';
import { useEffect, useMemo, useState } from 'react';

import { pinTarget } from './SearchPanel';
import type { StationTheme } from './stationTheme';

export interface MissionBoardProps {
  seed: string;
  systemId: string;
  stationId: string;
  theme: StationTheme;
  cargo: readonly CargoItem[];
  onCargoChange: (cargo: CargoItem[]) => void;
  wallet: Wallet;
  onWalletChange: (wallet: Wallet) => void;
  /** Hold space, so a crate that will not fit says so before it is taken. */
  capacity: number;
  /**
   * Whether this player is still being walked through their first job.
   *
   * The board shows exactly one offer while it is true — see `tutorialBoard`.
   * A first mission board with four jobs on it is a decision, and the
   * introduction is meant to be an instruction.
   */
  introducing?: boolean;
  /** Ask the server to do something. See `runAction`. */
  act: (path: string, body?: Record<string, unknown>) => Promise<boolean>;
  /**
   * A feature has been used, by name. See `lessons.ts`.
   *
   * Fired on a job actually taken rather than on the board being read: a
   * deadline is not a thing to explain to somebody browsing, and the clock does
   * not start until they commit.
   */
  onFeatureUsed?: (lessonId: string) => void;
}

/** A delivery, as it sits in the hold. */
export function MissionBoard({
  seed,
  systemId,
  stationId,
  theme,
  cargo,
  onCargoChange,
  act,
  wallet,
  onWalletChange,
  capacity,
  introducing = false,
  onFeatureUsed,
}: MissionBoardProps) {
  const [note, setNote] = useState<string | null>(null);

  /**
   * A clock, so deadlines count down while you look at them.
   *
   * Once a second is plenty for something measured in minutes, and it is the
   * only thing on this panel that changes on its own.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  /*
   * The board, seeded off the station and the hour so it holds still while it
   * is read — unless the introduction is running, in which case it is the one
   * job the introduction is about and does not rotate at all.
   *
   * The same choice the server makes when an accept arrives, from the same
   * functions, so the board drawn here is the board that will be honoured.
   */
  const offers = useMemo(
    () =>
      introducing
        ? tutorialBoard(seed, systemId as never, stationId)
        : stationMissions(seed, systemId as never, stationId),
    [seed, systemId, stationId, introducing],
  );

  /** Deliveries aboard, split by whether this station is the one that wants them. */
  const carried = cargo.filter((item) => item.mission !== undefined);
  const forHere = carried.filter((item) => missionRedeemable(item.mission!, stationId, now));

  const take = (brief: MissionBrief, route: boolean) => {
    // Checked here to avoid asking for something obviously impossible; the
    // server rolls the same board from the seed and decides for itself, so a
    // job that is not on it cannot be accepted however the request is shaped.
    void act('/missions/accept', { missionId: brief.id }).then((ok) => {
      // Refused. The reason is shown by the app — see `Refusal` — so this only
      // has to not carry on as though the job had been taken.
      if (!ok) return;

      // Taken, so the clock is running. Said now rather than when it runs out,
      // which is the one time it cannot be acted on.
      onFeatureUsed?.('mission-deadline');

      if (route) {
        /*
         * Pinned as a *body*, not as a system.
         *
         * A station is a body in the scene, listed under its own name, so a
         * body target keeps working after the last jump: the cockpit stops
         * pointing at the next system and starts pointing at the actual berth.
         */
        pinTarget({
          kind: 'body',
          label: brief.destination.stationName,
          detail: `delivery · ${brief.destination.systemName}`,
          systemId: brief.destination.systemId,
          clusterId: brief.destination.systemId.split('-')[0] as never,
          bodyName: brief.destination.stationName,
        });
      }
    });
  };

  const redeem = (item: CargoItem) => {
    // Everything needed is on the item — where it was going, what it pays, when
    // it stops counting — so the server checks the same thing this button was
    // drawn from. A deadline that passes between the two is refused, which is
    // the only race here and the right way to lose it.
    void act('/missions/redeem', { itemId: item.id });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {/* ── Owed to you here ─────────────────────────────────────────────── */}
      {forHere.length > 0 && (
        <div
          style={{
            border: `1px solid ${theme.accent}55`,
            background: `${theme.accent}12`,
            padding: '7px 8px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ fontSize: 10, color: theme.accent, letterSpacing: 1, textTransform: 'uppercase' }}>
            for delivery here
          </div>

          {forHere.map((item) => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ flex: 1, fontSize: 11.5 }}>{item.label}</span>
              <span style={{ fontSize: 11, color: theme.accent }}>
                {formatLinelits(item.mission!.reward)}L
              </span>
              <button type="button" onClick={() => redeem(item)} style={REDEEM}>
                redeem
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── What is aboard, and how long it has ──────────────────────────── */}
      {carried.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ fontSize: 10, color: theme.dim, letterSpacing: 1, textTransform: 'uppercase' }}>
            in the hold
          </div>

          {carried.map((item) => {
            const mission = item.mission!;
            const dead = missionExpired(mission, now);
            const here = mission.destination.stationId === stationId;

            return (
              <div key={item.id} style={{ fontSize: 11, color: dead ? '#ff5f6b' : theme.dim }}>
                {item.label} → {mission.destination.stationName}
                {!here && `, ${mission.destination.systemName}`}
                {' · '}
                {dead ? 'expired — sell it to be rid of it' : formatRemaining(missionRemaining(mission, now))}
              </div>
            );
          })}
        </div>
      )}

      {/* ── On offer ─────────────────────────────────────────────────────── */}
      {offers.length === 0 && (
        <div style={{ fontSize: 11, color: theme.dim }}>Nothing on the board right now.</div>
      )}

      {offers.map((brief) => {
        const taken = cargo.some((item) => item.id === `mission-${brief.id}`);

        return (
          <div
            key={brief.id}
            style={{
              borderTop: `1px solid ${theme.edge}`,
              paddingTop: 7,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              opacity: taken ? 0.45 : 1,
            }}
          >
            {/* The job as a sentence, then its numbers.
                A row of labelled figures with no verb in it reads as a table of
                stock; a delivery is an instruction, and saying it as one makes
                the destination the thing you remember rather than the reward. */}
            <div style={{ fontSize: 11.5, lineHeight: 1.45 }}>
              Deliver the <strong>{MISSION_LABELS[brief.kind]}</strong> to{' '}
              <strong style={{ color: theme.accent }}>{brief.destination.stationName}</strong> in{' '}
              <strong style={{ color: theme.accent }}>{brief.destination.systemName}</strong> System
            </div>

            <div style={{ fontSize: 10.5, color: theme.dim, lineHeight: 1.5 }}>
              <Figure label="Distance" value={brief.band} />
              {/* A crossing costs a core, and that is 150L off the reward — the
                  one cost that is not visible in any of the other figures. */}
              {brief.band === 'distant' && (
                <span style={{ color: '#ffb347' }}> (requires warp core)</span>
              )}
              {' || '}
              <Figure label="Cargo weight" value={`${brief.weight}w`} />
              {' || '}
              <Figure label="Reward" value={`${formatLinelits(brief.reward)}L`} />
              {' || '}
              {/* Null for the introduction's job, which has no clock at all —
                  and rendered "nullmin" until this said so. */}
              <Figure
                label="Time limit"
                value={brief.minutes === null ? 'none' : `${brief.minutes}min`}
              />
            </div>

            {taken ? (
              <div style={{ fontSize: 10.5, color: theme.dim }}>aboard</div>
            ) : (
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={() => take(brief, false)} style={accept(theme)}>
                  accept
                </button>
                <button type="button" onClick={() => take(brief, true)} style={accept(theme, true)}>
                  accept and route
                </button>
              </div>
            )}
          </div>
        );
      })}

      {note && <div style={{ fontSize: 11, color: '#ffb347' }}>{note}</div>}
    </div>
  );
}

/**
 * The one button on this panel that is money in your hand.
 *
 * Gold, and the only gold thing in the berth. Everything else here is drawn in
 * the station's own colours, which are a fact about *where you are* — this is a
 * fact about what you have just earned, and it should not have to compete with
 * a faction's paint for your attention. It is also the button a player has
 * flown a long way to press.
 */
const REDEEM: React.CSSProperties = {
  flex: 1,
  padding: '5px 10px',
  background: 'linear-gradient(180deg, #ffcf5a 0%, #ff9f2e 100%)',
  border: '1px solid #ffe9a8',
  boxShadow: '0 0 12px rgba(255,180,60,0.55)',
  color: '#2a1600',
  font: 'inherit',
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  cursor: 'pointer',
};

/** One labelled figure on a job's line of numbers. */
function Figure({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {label}: <span style={{ opacity: 0.95 }}>{value}</span>
    </span>
  );
}

/** Routing is the violet the chart and the cockpit both mark a target in. */
function accept(theme: StationTheme, routes = false): React.CSSProperties {
  return {
    flex: 1,
    padding: '4px 8px',
    background: routes ? 'rgba(190,120,255,0.16)' : 'transparent',
    border: `1px solid ${routes ? 'rgba(190,120,255,0.45)' : theme.edge}`,
    color: routes ? '#e8d8ff' : theme.accent,
    font: 'inherit',
    fontSize: 11,
    cursor: 'pointer',
  };
}
